import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DueDateField from "../../Compliance/AuditHub/DueDateField";
import {
  AH_ACCENT,
  AH_ACCENT_HOVER,
  AH_BORDER,
  AH_TEXT,
  AH_WHITE,
  accentOutlinedButtonSx,
  auditHubPageSx,
  primaryButtonSx,
} from "../../Compliance/AuditHub/theme";
import { listPartnerPrograms } from "../../../helpers/partnerCentral_helper";
import {
  buildSelectProps,
  menuItemSx,
  selectFieldSx,
} from "../../PartnerCentral/formFieldStyles";
import { resolveCommitmentMode } from "../../PartnerCentral/customerPayload";
import WholesaleProgramsField from "../../PartnerCentral/WholesaleProgramsField";

const commitmentModeToggleSx = {
  flexWrap: "wrap",
  "& .MuiToggleButton-root": {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8125rem",
    px: 1.5,
    py: 0.75,
    color: AH_TEXT,
    borderColor: AH_BORDER,
    "&.Mui-selected": {
      backgroundColor: AH_ACCENT,
      color: "#fff",
      borderColor: AH_ACCENT,
      "&:hover": { backgroundColor: AH_ACCENT_HOVER },
    },
  },
};

const nestedDialogPaperSx = {
  ...auditHubPageSx,
  borderRadius: "12px",
  overflow: "hidden",
  maxWidth: 560,
};

const nestedDialogHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  minHeight: 56,
  px: 3,
  backgroundColor: AH_ACCENT,
};

const nestedDialogCloseSx = {
  p: 0,
  m: 0,
  border: "none",
  background: "transparent",
  color: AH_WHITE,
  fontSize: 28,
  fontWeight: 700,
  lineHeight: 1,
  cursor: "pointer",
  flexShrink: 0,
};

const nestedDialogFooterSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 1.5,
  px: 3,
  py: 2,
  borderTop: `1px solid ${AH_BORDER}`,
  backgroundColor: "#f8fafc",
};

const nestedDialogCancelSx = {
  ...primaryButtonSx,
  height: 36,
  minHeight: 36,
  backgroundColor: "#74788d",
  "&:hover": { backgroundColor: "#5f6478", boxShadow: "none" },
};

const nestedDialogPrimarySx = {
  ...primaryButtonSx,
  height: 36,
  minHeight: 36,
};

const PAYMENT_TERMS = ["Net 30", "Net 45", "Net 60"];

const PRICING_MODELS = [
  "Standard List Minus Margin",
  "Cost-Plus Markup",
  "Custom Rate Card",
];

const SERVICE_EXCLUSIONS = [
  "Exclude AWS Marketplace",
  "Exclude Support Fees",
  "Exclude Taxes",
];

const labelSx = {
  display: "block",
  mb: 0.75,
  fontSize: 13,
  fontWeight: 600,
  color: "#1e293b",
  lineHeight: 1.4,
};

const sectionSx = {
  fontSize: 14,
  fontWeight: 700,
  color: "#1e293b",
  letterSpacing: "-0.01em",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
  },
};

const dayAfterIso = (isoDate) => {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return null;
  const date = new Date(`${isoDate}T12:00:00`);
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
};

const parseNumber = (value) => {
  const cleaned = String(value || "")
    .replace(/[$,%/]/g, "")
    .trim();
  if (!cleaned) return null;
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : NaN;
};

const formatCommitment = (value) => {
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return String(value || "");
  return amount.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const formatDisplayDate = (isoDate) => {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return "—";
  const [y, m, d] = isoDate.split("-");
  return `${m}/${d}/${y}`;
};

const emptyYear = () => ({
  id: `year-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  label: "",
  startDate: "",
  endDate: "",
  commitment: "",
  discount: "",
});

const emptyTier = () => ({
  id: `tier-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name: "",
  threshold: "",
  discount: "",
});

export const emptyCommercialForm = () => ({
  customerId: "",
  regions: [],
  wholesalePrograms: [],
  startDate: "",
  endDate: "",
  paymentTerms: "",
  multiYearCommitment: "",
  commitmentDiscount: "",
  pricingModel: "",
  serviceExclusions: [],
  commitmentMode: "annual",
  years: [],
  tiers: [],
});

const FieldLabel = ({ htmlFor, children, required }) => (
  <Typography component="label" htmlFor={htmlFor} sx={labelSx}>
    {children}
    {required ? (
      <Box component="span" sx={{ color: "#dc2626", ml: 0.4 }} aria-hidden="true">
        *
      </Box>
    ) : null}
  </Typography>
);

FieldLabel.propTypes = {
  htmlFor: PropTypes.string,
  children: PropTypes.node.isRequired,
  required: PropTypes.bool,
};

/**
 * Partner Central commercial fields for FinOps Add Customer (create-only).
 * Controlled via value/onChange; errors keyed like Partner Central validate().
 */
const FinOpsCustomerCommercialSection = ({ value, onChange, errors = {} }) => {
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [programsError, setProgramsError] = useState("");

  const [yearDialogOpen, setYearDialogOpen] = useState(false);
  const [editingYearId, setEditingYearId] = useState(null);
  const [yearDraft, setYearDraft] = useState(emptyYear());
  const [yearDraftErrors, setYearDraftErrors] = useState({});

  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [editingTierId, setEditingTierId] = useState(null);
  const [tierDraft, setTierDraft] = useState(emptyTier());
  const [tierDraftErrors, setTierDraftErrors] = useState({});

  const setField = (key, nextValue) => {
    onChange({ ...value, [key]: nextValue });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setProgramsLoading(true);
      setProgramsError("");
      try {
        const programs = await listPartnerPrograms({
          statuses: ["DRAFT", "ACTIVE"],
        });
        if (!cancelled)
          setAvailablePrograms(Array.isArray(programs) ? programs : []);
      } catch (error) {
        if (!cancelled) {
          setAvailablePrograms([]);
          setProgramsError(
            error?.message || "Failed to load wholesale programs.",
          );
        }
      } finally {
        if (!cancelled) setProgramsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleExclusion = (label) => {
    const exclusions = value.serviceExclusions || [];
    setField(
      "serviceExclusions",
      exclusions.includes(label)
        ? exclusions.filter((item) => item !== label)
        : [...exclusions, label],
    );
  };

  const openYearDialog = () => {
    setEditingYearId(null);
    setYearDraft(emptyYear());
    setYearDraftErrors({});
    setYearDialogOpen(true);
  };

  const openEditYearDialog = (year) => {
    setEditingYearId(year.id);
    setYearDraft({ ...year });
    setYearDraftErrors({});
    setYearDialogOpen(true);
  };

  const closeYearDialog = () => {
    setYearDialogOpen(false);
    setEditingYearId(null);
    setYearDraftErrors({});
  };

  const saveYearDraft = () => {
    const nextErrors = {};
    if (!yearDraft.label.trim())
      nextErrors.label = "Enter a program year identifier.";
    if (
      yearDraft.startDate &&
      yearDraft.endDate &&
      yearDraft.endDate < yearDraft.startDate
    ) {
      nextErrors.endDate = "End date is before the start date.";
    }
    const commitment = parseNumber(yearDraft.commitment);
    if (
      yearDraft.commitment.trim() &&
      (Number.isNaN(commitment) || commitment < 0)
    ) {
      nextErrors.commitment = "Enter an amount.";
    }
    const discount = parseNumber(yearDraft.discount);
    if (
      yearDraft.discount.trim() &&
      (Number.isNaN(discount) || discount < 0 || discount > 100)
    ) {
      nextErrors.discount = "Enter 0 to 100.";
    }
    setYearDraftErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const normalized = {
      ...yearDraft,
      label: yearDraft.label.trim(),
      commitment: yearDraft.commitment.trim()
        ? formatCommitment(yearDraft.commitment)
        : "",
      discount: yearDraft.discount.trim(),
    };

    const years = value.years || [];
    setField(
      "years",
      editingYearId
        ? years.map((row) =>
            row.id === editingYearId
              ? { ...normalized, id: editingYearId }
              : row,
          )
        : [...years, normalized],
    );
    closeYearDialog();
  };

  const openTierDialog = () => {
    setEditingTierId(null);
    setTierDraft(emptyTier());
    setTierDraftErrors({});
    setTierDialogOpen(true);
  };

  const openEditTierDialog = (tier) => {
    setEditingTierId(tier.id);
    setTierDraft({ ...tier });
    setTierDraftErrors({});
    setTierDialogOpen(true);
  };

  const closeTierDialog = () => {
    setTierDialogOpen(false);
    setEditingTierId(null);
    setTierDraftErrors({});
  };

  const saveTierDraft = () => {
    const nextErrors = {};
    if (!tierDraft.name.trim()) nextErrors.name = "Enter a tier name.";
    const threshold = parseNumber(tierDraft.threshold);
    if (
      tierDraft.threshold.trim() &&
      (Number.isNaN(threshold) || threshold < 0)
    ) {
      nextErrors.threshold = "Enter an amount.";
    }
    const discount = parseNumber(tierDraft.discount);
    if (
      tierDraft.discount.trim() &&
      (Number.isNaN(discount) || discount < 0 || discount > 100)
    ) {
      nextErrors.discount = "Enter 0 to 100.";
    }
    setTierDraftErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const normalized = {
      ...tierDraft,
      name: tierDraft.name.trim(),
      threshold: tierDraft.threshold.trim()
        ? formatCommitment(tierDraft.threshold)
        : "",
      discount: tierDraft.discount.trim(),
    };

    const tiers = value.tiers || [];
    setField(
      "tiers",
      editingTierId
        ? tiers.map((row) =>
            row.id === editingTierId
              ? { ...normalized, id: editingTierId }
              : row,
          )
        : [...tiers, normalized],
    );
    closeTierDialog();
  };

  return (
    <>
      <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #e2e8f0" }}>
        <Typography sx={{ ...sectionSx, mb: 1.5 }}>
          Commercial Contract Details
        </Typography>

        <Box sx={{ display: "grid", gap: 2 }}>
          <WholesaleProgramsField
            id="pc-wholesalePrograms"
            options={availablePrograms}
            value={value.wholesalePrograms || []}
            onChange={(next) => setField("wholesalePrograms", next)}
            loading={programsLoading}
            error={programsError}
            fieldError={errors.wholesalePrograms || ""}
            helperText="Programs governing the upstream discount for this customer"
            fieldSx={fieldSx}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="pc-startDate" required>
                Contract Start Date
              </FieldLabel>
              <DueDateField
                id="pc-startDate"
                value={value.startDate || ""}
                onChange={(next) => {
                  const patch = { startDate: next };
                  if (value.endDate && next && value.endDate <= next)
                    patch.endDate = "";
                  onChange({ ...value, ...patch });
                }}
                fullWidth
                showLabel={false}
                size="small"
                minDate={null}
                required
                ariaLabel="Contract start date"
                placeholder="Select the Start Date"
                error={Boolean(errors.startDate)}
                helperText={errors.startDate || ""}
                portalId="root"
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="pc-endDate" required>
                Contract End Date
              </FieldLabel>
              <DueDateField
                id="pc-endDate"
                value={value.endDate || ""}
                onChange={(next) => setField("endDate", next)}
                fullWidth
                showLabel={false}
                size="small"
                minDate={dayAfterIso(value.startDate)}
                required
                ariaLabel="Contract end date"
                placeholder="Select the End Date"
                error={Boolean(errors.endDate)}
                helperText={errors.endDate || ""}
                portalId="root"
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="pc-paymentTerms">Payment Terms</FieldLabel>
              <TextField
                id="pc-paymentTerms"
                select
                size="small"
                fullWidth
                value={value.paymentTerms || ""}
                onChange={(event) =>
                  setField("paymentTerms", event.target.value)
                }
                error={Boolean(errors.paymentTerms)}
                helperText={errors.paymentTerms || ""}
                sx={selectFieldSx(Boolean(value.paymentTerms))}
                SelectProps={buildSelectProps({
                  placeholder: "Select terms...",
                })}
              >
                {PAYMENT_TERMS.map((term) => (
                  <MenuItem key={term} value={term} sx={menuItemSx}>
                    {term}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          <Typography sx={{ ...sectionSx, mt: 1 }}>
            Commitment Details
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="pc-multiYearCommitment">
                Multi-Year Commitment Amount ($)
              </FieldLabel>
              <TextField
                id="pc-multiYearCommitment"
                size="small"
                fullWidth
                value={value.multiYearCommitment || ""}
                onChange={(event) =>
                  setField("multiYearCommitment", event.target.value)
                }
                onBlur={() => {
                  if (
                    !value.multiYearCommitment?.trim() ||
                    errors.multiYearCommitment
                  )
                    return;
                  const amount = parseNumber(value.multiYearCommitment);
                  if (amount != null && !Number.isNaN(amount)) {
                    setField(
                      "multiYearCommitment",
                      formatCommitment(value.multiYearCommitment),
                    );
                  }
                }}
                placeholder="1,500,000"
                error={Boolean(errors.multiYearCommitment)}
                helperText={
                  errors.multiYearCommitment ||
                  "Overall multi-year spend obligation across the full contract term"
                }
                inputProps={{ inputMode: "decimal" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="pc-commitmentDiscount">
                Discount (%)
              </FieldLabel>
              <TextField
                id="pc-commitmentDiscount"
                size="small"
                fullWidth
                value={value.commitmentDiscount || ""}
                onChange={(event) =>
                  setField("commitmentDiscount", event.target.value)
                }
                placeholder="0.00"
                error={Boolean(errors.commitmentDiscount)}
                helperText={errors.commitmentDiscount || ""}
                inputProps={{ inputMode: "decimal" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                sx={fieldSx}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 0.5 }}>
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b", mb: 1 }}
            >
              Commitment Structure
            </Typography>
            <Typography
              sx={{ fontSize: 12, color: "#64748b", mb: 1.25, lineHeight: 1.4 }}
            >
              Choose one structure for this customer. Only the selected option
              is saved.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 1.25,
              }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={resolveCommitmentMode(value)}
                onChange={(_event, next) => {
                  if (!next) return;
                  // Keep both drafts in memory while switching; only the active mode is saved.
                  onChange({
                    ...value,
                    commitmentMode: next,
                  });
                }}
                aria-label="Commitment structure"
                sx={commitmentModeToggleSx}
              >
                <ToggleButton value="annual">
                  Annual Commitment Breakdown
                </ToggleButton>
                <ToggleButton value="tiers">
                  Downstream Volume Discount Tiers
                </ToggleButton>
              </ToggleButtonGroup>
              {resolveCommitmentMode(value) === "annual" ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  onClick={openYearDialog}
                  sx={{
                    ...accentOutlinedButtonSx,
                    height: 36,
                    minHeight: 36,
                    px: 1.5,
                    flexShrink: 0,
                  }}
                >
                  Add Year
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  onClick={openTierDialog}
                  sx={{
                    ...accentOutlinedButtonSx,
                    height: 36,
                    minHeight: 36,
                    px: 1.5,
                    flexShrink: 0,
                  }}
                >
                  Add Tier
                </Button>
              )}
            </Box>

            {resolveCommitmentMode(value) === "annual" ? (
              <TableContainer
                sx={{ border: "1px solid #e2e8f0", borderRadius: "8px" }}
              >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        {[
                          "YEAR",
                          "START",
                          "END",
                          "COMMITMENT ($)",
                          "DISCOUNT (%)",
                          "",
                        ].map((heading) => (
                          <TableCell
                            key={heading || "actions"}
                            sx={{
                              fontSize: "0.6875rem",
                              fontWeight: 700,
                              color: "#64748b",
                              borderBottom: "1px solid #e2e8f0",
                            }}
                          >
                            {heading}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!value.years?.length ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            sx={{
                              py: 3,
                              color: "#64748b",
                              fontSize: "0.8125rem",
                            }}
                          >
                            No annual commitment rows yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        value.years.map((year) => {
                          const rowErrors = errors.years?.[year.id] || {};
                          return (
                            <TableRow key={year.id} hover>
                              <TableCell
                                sx={{ fontSize: "0.8125rem", fontWeight: 600 }}
                              >
                                {year.label || "—"}
                                {rowErrors.label ? (
                                  <Typography
                                    sx={{
                                      mt: 0.25,
                                      fontSize: 12,
                                      color: "#d32f2f",
                                    }}
                                  >
                                    {rowErrors.label}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8125rem" }}>
                                {formatDisplayDate(year.startDate)}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8125rem" }}>
                                {formatDisplayDate(year.endDate)}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8125rem" }}>
                                {year.commitment ? `$${year.commitment}` : "—"}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8125rem" }}>
                                {year.discount ? `${year.discount}%` : "—"}
                              </TableCell>
                              <TableCell
                                sx={{ whiteSpace: "nowrap", width: 88 }}
                              >
                                <Tooltip title="Edit year">
                                  <IconButton
                                    size="small"
                                    aria-label={`Edit ${year.label || "year"}`}
                                    onClick={() => openEditYearDialog(year)}
                                  >
                                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove year">
                                  <IconButton
                                    size="small"
                                    aria-label={`Remove ${year.label || "year"}`}
                                    onClick={() =>
                                      setField(
                                        "years",
                                        value.years.filter(
                                          (row) => row.id !== year.id,
                                        ),
                                      )
                                    }
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
              </TableContainer>
            ) : (
              <TableContainer
                sx={{ border: "1px solid #e2e8f0", borderRadius: "8px" }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      {["TIER NAME", "MIN SPEND ($)", "DISCOUNT (%)", ""].map(
                        (heading) => (
                          <TableCell
                            key={heading || "actions"}
                            sx={{
                              fontSize: "0.6875rem",
                              fontWeight: 700,
                              color: "#64748b",
                              borderBottom: "1px solid #e2e8f0",
                            }}
                          >
                            {heading}
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  </TableHead>
                    <TableBody>
                      {!value.tiers?.length ? (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{
                              py: 3,
                              color: "#64748b",
                              fontSize: "0.8125rem",
                            }}
                          >
                            No volume discount tiers yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        value.tiers.map((tier) => {
                          const rowErrors = errors.tiers?.[tier.id] || {};
                          return (
                            <TableRow key={tier.id} hover>
                              <TableCell
                                sx={{ fontSize: "0.8125rem", fontWeight: 600 }}
                              >
                                {tier.name || "—"}
                                {rowErrors.name ? (
                                  <Typography
                                    sx={{
                                      mt: 0.25,
                                      fontSize: 12,
                                      color: "#d32f2f",
                                    }}
                                  >
                                    {rowErrors.name}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8125rem" }}>
                                {tier.threshold ? `$${tier.threshold}` : "—"}
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8125rem" }}>
                                {tier.discount ? `${tier.discount}%` : "—"}
                              </TableCell>
                              <TableCell
                                sx={{ whiteSpace: "nowrap", width: 88 }}
                              >
                                <Tooltip title="Edit tier">
                                  <IconButton
                                    size="small"
                                    aria-label={`Edit ${tier.name || "tier"}`}
                                    onClick={() => openEditTierDialog(tier)}
                                  >
                                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove tier">
                                  <IconButton
                                    size="small"
                                    aria-label={`Remove ${tier.name || "tier"}`}
                                    onClick={() =>
                                      setField(
                                        "tiers",
                                        value.tiers.filter(
                                          (row) => row.id !== tier.id,
                                        ),
                                      )
                                    }
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
              </TableContainer>
            )}
          </Box>

          <Typography sx={{ ...sectionSx, mt: 1 }}>Pricing Model</Typography>

          <Box sx={{ minWidth: 0 }}>
            <FieldLabel htmlFor="pc-pricingModel">Pricing Model</FieldLabel>
            <TextField
              id="pc-pricingModel"
              select
              size="small"
              fullWidth
              value={value.pricingModel || ""}
              onChange={(event) => setField("pricingModel", event.target.value)}
              error={Boolean(errors.pricingModel)}
              helperText={
                errors.pricingModel ||
                "Defines how downstream retail pricing is calculated from wholesale rates"
              }
              sx={selectFieldSx(Boolean(value.pricingModel))}
              SelectProps={buildSelectProps({
                placeholder: "Select pricing model...",
              })}
            >
              {PRICING_MODELS.map((model) => (
                <MenuItem key={model} value={model} sx={menuItemSx}>
                  {model}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Accordion
            disableGutters
            elevation={0}
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px !important",
              "&:before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#64748b" }} />}
              sx={{
                minHeight: 48,
                px: 1.5,
                "& .MuiAccordionSummary-content": {
                  my: 1,
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  pr: 1,
                },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}
                >
                  Service Exclusions
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.25 }}>
                  Block discount application for selected AWS service categories
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#64748b",
                  flexShrink: 0,
                }}
              >
                {value.serviceExclusions?.length
                  ? `${value.serviceExclusions.length} selected`
                  : "None selected"}
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{ px: 1.5, pt: 0, pb: 1.5, borderTop: "1px solid #e2e8f0" }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, pt: 1.5 }}>
                {SERVICE_EXCLUSIONS.map((label) => {
                  const selected = (value.serviceExclusions || []).includes(
                    label,
                  );
                  return (
                    <Chip
                      key={label}
                      label={label}
                      clickable
                      onClick={() => toggleExclusion(label)}
                      sx={{
                        fontWeight: 500,
                        backgroundColor: selected ? "#0f172a" : "#f8fafc",
                        color: selected ? "#fff" : "#475569",
                        border: "1px solid #e2e8f0",
                      }}
                    />
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>

      <Dialog
        open={yearDialogOpen}
        onClose={closeYearDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: nestedDialogPaperSx }}
      >
        <Box sx={nestedDialogHeaderSx}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.0625rem",
              color: AH_WHITE,
              lineHeight: 1.3,
            }}
          >
            {editingYearId ? "Edit Program Year" : "Add Program Year"}
          </Typography>
          <Box
            component="button"
            type="button"
            aria-label="Close"
            onClick={closeYearDialog}
            sx={nestedDialogCloseSx}
          >
            ×
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
          <Box sx={{ display: "grid", gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="year-draft-label">
                Program Year Identifier
              </FieldLabel>
              <TextField
                id="year-draft-label"
                size="small"
                fullWidth
                value={yearDraft.label}
                onChange={(event) =>
                  setYearDraft((prev) => ({
                    ...prev,
                    label: event.target.value,
                  }))
                }
                placeholder="e.g., Year 1"
                error={Boolean(yearDraftErrors.label)}
                helperText={yearDraftErrors.label || ""}
                sx={fieldSx}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel htmlFor="year-draft-start">Start Date</FieldLabel>
                <DueDateField
                  id="year-draft-start"
                  value={yearDraft.startDate}
                  onChange={(next) =>
                    setYearDraft((prev) => ({ ...prev, startDate: next }))
                  }
                  fullWidth
                  showLabel={false}
                  size="small"
                  minDate={null}
                  ariaLabel="Annual commitment start date"
                  placeholder="Select the Start Date"
                  portalId="root"
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel htmlFor="year-draft-end">End Date</FieldLabel>
                <DueDateField
                  id="year-draft-end"
                  value={yearDraft.endDate}
                  onChange={(next) =>
                    setYearDraft((prev) => ({ ...prev, endDate: next }))
                  }
                  fullWidth
                  showLabel={false}
                  size="small"
                  minDate={dayAfterIso(yearDraft.startDate)}
                  ariaLabel="Annual commitment end date"
                  placeholder="Select the End Date"
                  error={Boolean(yearDraftErrors.endDate)}
                  helperText={yearDraftErrors.endDate || ""}
                  portalId="root"
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel htmlFor="year-draft-commitment">
                  Commitment ($)
                </FieldLabel>
                <TextField
                  id="year-draft-commitment"
                  size="small"
                  fullWidth
                  value={yearDraft.commitment}
                  onChange={(event) =>
                    setYearDraft((prev) => ({
                      ...prev,
                      commitment: event.target.value,
                    }))
                  }
                  error={Boolean(yearDraftErrors.commitment)}
                  helperText={yearDraftErrors.commitment || ""}
                  inputProps={{ inputMode: "decimal" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel htmlFor="year-draft-discount">
                  Discount (%)
                </FieldLabel>
                <TextField
                  id="year-draft-discount"
                  size="small"
                  fullWidth
                  value={yearDraft.discount}
                  onChange={(event) =>
                    setYearDraft((prev) => ({
                      ...prev,
                      discount: event.target.value,
                    }))
                  }
                  error={Boolean(yearDraftErrors.discount)}
                  helperText={yearDraftErrors.discount || ""}
                  inputProps={{ inputMode: "decimal" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <Box sx={nestedDialogFooterSx}>
          <Button
            variant="contained"
            onClick={closeYearDialog}
            sx={nestedDialogCancelSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveYearDraft}
            sx={nestedDialogPrimarySx}
          >
            {editingYearId ? "Update Year" : "Add Year"}
          </Button>
        </Box>
      </Dialog>

      <Dialog
        open={tierDialogOpen}
        onClose={closeTierDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: nestedDialogPaperSx }}
      >
        <Box sx={nestedDialogHeaderSx}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.0625rem",
              color: AH_WHITE,
              lineHeight: 1.3,
            }}
          >
            {editingTierId ? "Edit Volume Tier" : "Add Volume Tier"}
          </Typography>
          <Box
            component="button"
            type="button"
            aria-label="Close"
            onClick={closeTierDialog}
            sx={nestedDialogCloseSx}
          >
            ×
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
          <Box sx={{ display: "grid", gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="tier-draft-name">Tier Name</FieldLabel>
              <TextField
                id="tier-draft-name"
                size="small"
                fullWidth
                value={tierDraft.name}
                onChange={(event) =>
                  setTierDraft((prev) => ({
                    ...prev,
                    name: event.target.value,
                  }))
                }
                error={Boolean(tierDraftErrors.name)}
                helperText={tierDraftErrors.name || ""}
                sx={fieldSx}
              />
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel htmlFor="tier-draft-threshold">
                  Min Spend Threshold ($)
                </FieldLabel>
                <TextField
                  id="tier-draft-threshold"
                  size="small"
                  fullWidth
                  value={tierDraft.threshold}
                  onChange={(event) =>
                    setTierDraft((prev) => ({
                      ...prev,
                      threshold: event.target.value,
                    }))
                  }
                  error={Boolean(tierDraftErrors.threshold)}
                  helperText={tierDraftErrors.threshold || ""}
                  inputProps={{ inputMode: "decimal" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel htmlFor="tier-draft-discount">
                  Retail Discount (%)
                </FieldLabel>
                <TextField
                  id="tier-draft-discount"
                  size="small"
                  fullWidth
                  value={tierDraft.discount}
                  onChange={(event) =>
                    setTierDraft((prev) => ({
                      ...prev,
                      discount: event.target.value,
                    }))
                  }
                  error={Boolean(tierDraftErrors.discount)}
                  helperText={tierDraftErrors.discount || ""}
                  inputProps={{ inputMode: "decimal" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <Box sx={nestedDialogFooterSx}>
          <Button
            variant="contained"
            onClick={closeTierDialog}
            sx={nestedDialogCancelSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveTierDraft}
            sx={nestedDialogPrimarySx}
          >
            {editingTierId ? "Update Tier" : "Add Tier"}
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

FinOpsCustomerCommercialSection.propTypes = {
  value: PropTypes.shape({
    customerId: PropTypes.string,
    regions: PropTypes.arrayOf(PropTypes.string),
    wholesalePrograms: PropTypes.arrayOf(PropTypes.object),
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    paymentTerms: PropTypes.string,
    multiYearCommitment: PropTypes.string,
    commitmentDiscount: PropTypes.string,
    pricingModel: PropTypes.string,
    serviceExclusions: PropTypes.arrayOf(PropTypes.string),
    commitmentMode: PropTypes.oneOf(["annual", "tiers"]),
    years: PropTypes.arrayOf(PropTypes.object),
    tiers: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default FinOpsCustomerCommercialSection;
